import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const TrainingProgramsTab = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categoryOptions = [
    { value: 'all', label: 'All Categories' },
    { value: 'technical', label: 'Technical Skills' },
    { value: 'soft-skills', label: 'Soft Skills' },
    { value: 'industry', label: 'Industry Certifications' },
    { value: 'placement', label: 'Placement Preparation' }
  ];

  const programs = [
    {
      id: 1,
      name: 'Full Stack Development Bootcamp',
      category: 'Technical Skills',
      duration: '12 weeks',
      enrolled: 80,
      completed: 45,
      inProgress: 35,
      instructor: 'Prof. Rajesh Kumar',
      startDate: '2026-01-15',
      endDate: '2026-04-10',
      certificationRate: 92,
      skills: ['React', 'Node.js', 'MongoDB', 'Express'],
      status: 'Active'
    },
    {
      id: 2,
      name: 'Data Science & Machine Learning',
      category: 'Technical Skills',
      duration: '16 weeks',
      enrolled: 65,
      completed: 30,
      inProgress: 35,
      instructor: 'Dr. Priya Sharma',
      startDate: '2026-01-10',
      endDate: '2026-05-05',
      certificationRate: 88,
      skills: ['Python', 'TensorFlow', 'Pandas', 'Scikit-learn'],
      status: 'Active'
    },
    {
      id: 3,
      name: 'Communication & Leadership Skills',
      category: 'Soft Skills',
      duration: '8 weeks',
      enrolled: 120,
      completed: 95,
      inProgress: 25,
      instructor: 'Ms. Ananya Singh',
      startDate: '2026-01-20',
      endDate: '2026-03-15',
      certificationRate: 95,
      skills: ['Public Speaking', 'Team Management', 'Negotiation'],
      status: 'Active'
    },
    {
      id: 4,
      name: 'AWS Cloud Practitioner Certification',
      category: 'Industry Certifications',
      duration: '6 weeks',
      enrolled: 55,
      completed: 42,
      inProgress: 13,
      instructor: 'Mr. Amit Patel',
      startDate: '2025-12-01',
      endDate: '2026-01-15',
      certificationRate: 85,
      skills: ['AWS', 'Cloud Computing', 'DevOps'],
      status: 'Completed'
    }
  ];

  const competencyMapping = [
    {
      id: 1,
      skill: 'Full Stack Development',
      totalStudents: 245,
      beginner: 80,
      intermediate: 120,
      advanced: 45,
      industryDemand: 'High'
    },
    {
      id: 2,
      skill: 'Data Science',
      totalStudents: 180,
      beginner: 95,
      intermediate: 65,
      advanced: 20,
      industryDemand: 'Very High'
    },
    {
      id: 3,
      skill: 'Cloud Computing',
      totalStudents: 165,
      beginner: 70,
      intermediate: 75,
      advanced: 20,
      industryDemand: 'High'
    },
    {
      id: 4,
      skill: 'Mobile App Development',
      totalStudents: 140,
      beginner: 60,
      intermediate: 60,
      advanced: 20,
      industryDemand: 'Medium'
    }
  ];

  const getStatusColor = (status) => {
    const colors = {
      'Active': 'bg-success/10 text-success border-success/20',
      'Completed': 'bg-primary/10 text-primary border-primary/20',
      'Upcoming': 'bg-warning/10 text-warning border-warning/20'
    };
    return colors?.[status] || 'bg-muted text-muted-foreground';
  };

  const getDemandColor = (demand) => {
    const colors = {
      'Very High': 'text-success',
      'High': 'text-primary',
      'Medium': 'text-warning',
      'Low': 'text-error'
    };
    return colors?.[demand] || 'text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row gap-4">
        <Select
          options={categoryOptions}
          value={selectedCategory}
          onChange={setSelectedCategory}
          className="w-full md:w-64"
        />
        <Button variant="default" iconName="Plus">
          Create New Program
        </Button>
        <Button variant="outline" iconName="Upload">
          Import Curriculum
        </Button>
        <Button variant="outline" iconName="Download">
          Export Report
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Active Programs</p>
            <Icon name="BookOpen" size={20} color="var(--color-primary)" />
          </div>
          <p className="text-2xl font-bold text-primary">{programs?.filter(p => p?.status === 'Active')?.length}</p>
        </div>
        <div className="bg-success/5 rounded-lg p-4 border border-success/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Total Enrolled</p>
            <Icon name="Users" size={20} color="var(--color-success)" />
          </div>
          <p className="text-2xl font-bold text-success">{programs?.reduce((sum, p) => sum + p?.enrolled, 0)}</p>
        </div>
        <div className="bg-accent/5 rounded-lg p-4 border border-accent/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Completed</p>
            <Icon name="CheckCircle" size={20} color="var(--color-accent)" />
          </div>
          <p className="text-2xl font-bold text-accent">{programs?.reduce((sum, p) => sum + p?.completed, 0)}</p>
        </div>
        <div className="bg-warning/5 rounded-lg p-4 border border-warning/20">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-muted-foreground">Avg. Certification</p>
            <Icon name="Award" size={20} color="var(--color-warning)" />
          </div>
          <p className="text-2xl font-bold text-warning">
            {Math.round(programs?.reduce((sum, p) => sum + p?.certificationRate, 0) / programs?.length)}%
          </p>
        </div>
      </div>

      <div className="bg-background rounded-lg p-6 border border-border">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Training Programs</h3>
        <div className="space-y-4">
          {programs?.map((program) => (
            <div key={program?.id} className="p-4 rounded-lg border border-border hover:border-primary transition-smooth">
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="text-base font-medium text-foreground">{program?.name}</h4>
                      <p className="text-sm text-muted-foreground">Instructor: {program?.instructor}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(program?.status)}`}>
                      {program?.status}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-muted-foreground">Duration</p>
                      <p className="text-sm font-semibold text-foreground">{program?.duration}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Enrolled</p>
                      <p className="text-sm font-semibold text-foreground">{program?.enrolled}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Completed</p>
                      <p className="text-sm font-semibold text-success">{program?.completed}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Certification Rate</p>
                      <p className="text-sm font-semibold text-foreground">{program?.certificationRate}%</p>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {program?.skills?.map((skill, idx) => (
                      <span key={idx} className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                        {skill}
                      </span>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" iconName="Eye">
                      View Curriculum
                    </Button>
                    <Button variant="outline" size="sm" iconName="Users">
                      Manage Students
                    </Button>
                    <Button variant="outline" size="sm" iconName="BarChart3">
                      Progress Report
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-background rounded-lg p-6 border border-border">
        <h3 className="text-lg font-heading font-semibold text-foreground mb-4">Competency Mapping</h3>
        <div className="space-y-4">
          {competencyMapping?.map((competency) => (
            <div key={competency?.id} className="p-4 rounded-lg border border-border">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h4 className="text-base font-medium text-foreground">{competency?.skill}</h4>
                  <p className="text-sm text-muted-foreground">{competency?.totalStudents} students</p>
                </div>
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Industry Demand</p>
                  <p className={`text-sm font-semibold ${getDemandColor(competency?.industryDemand)}`}>
                    {competency?.industryDemand}
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-24">Beginner</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-error transition-smooth"
                      style={{ width: `${(competency?.beginner / competency?.totalStudents) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground w-12 text-right">{competency?.beginner}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-24">Intermediate</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-warning transition-smooth"
                      style={{ width: `${(competency?.intermediate / competency?.totalStudents) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground w-12 text-right">{competency?.intermediate}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs text-muted-foreground w-24">Advanced</span>
                  <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-success transition-smooth"
                      style={{ width: `${(competency?.advanced / competency?.totalStudents) * 100}%` }}
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground w-12 text-right">{competency?.advanced}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TrainingProgramsTab;